import { Claim, ClaimLevelEnum, Company, LoginResponse, ServerCall } from '@akiba/models_beta_temp';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ViewerInfo } from '../misc/classes';
import { LocalHelpers } from '../misc/localHelpers';
import { LocalStorage } from '../misc/localStorage';
import { HTTPRequestService } from './httpRequest.service';

@Injectable({
  providedIn: 'any'
})
export class AuthService {

  viewerInfo: BehaviorSubject<ViewerInfo | undefined> = new BehaviorSubject<ViewerInfo | undefined>(undefined);
  authSubscription: Subscription | undefined = undefined;
  constructor(public auth: AngularFireAuth, private req: HTTPRequestService, private router: Router) { }

  loginListener(): void {

    this.auth.onAuthStateChanged(async auth => {

      if (auth) {
        await this.router.navigate(['/auth']);
      }

    });

  }

  redirectListener(): void {

    this.auth.onAuthStateChanged(async auth => {

      if (auth) {
        const idToken = await this.idTokenResult();
        if (idToken) {

          const claim: Claim = new Claim(idToken.claims.claim);
          const vi = new ViewerInfo();
          const loaded = vi.loadFromSession();

          if (loaded) {

            let companyID: string | undefined = vi.company?.companyID;
            let uid: string = vi.uid;

            const path = this.pathForClaimLevel(claim.maxClaimLevel, companyID, uid);
            await this.router.navigate(path);

          } else {

            const data = await this.validateToken(idToken.token);
            if (data.successful) {

              vi.loadFromLoginResult(data.result);
              vi.saveForSession();

              let companyID: string | undefined = vi.company?.companyID;
              let uid: string = vi.uid;
              const path = this.pathForClaimLevel(claim.maxClaimLevel, companyID, uid);
              await this.router.navigate(path);

            } else {
              this.logOut();
            }

          }

        }
      }

    });

  }

  authListener(activeRoute: ActivatedRoute | undefined = undefined): void {

    if (this.authSubscription) {
      LocalHelpers.unsubscribe(this.authSubscription);
    }

    this.authSubscription = this.auth.authState.subscribe(async auth => {
      if (auth) {
        const idToken = await this.idTokenResult();
        if (idToken) {

          const claim: Claim = new Claim(idToken.claims.claim);
          const loadByParams = activeRoute ? await this.shouldLoadFromParams(activeRoute.parent!, claim) : false;

          if (loadByParams && activeRoute) {

            const paramsQ = activeRoute.params.pipe(take(1));
            const params: Params = await lastValueFrom(paramsQ);

            const vi: ViewerInfo = new ViewerInfo();

            const loaded = vi.loadFromSession();
            if (!loaded) {
              vi.loadFromParams(params);
            }

            this.viewerInfo.next(vi);

          } else {

            const vi: ViewerInfo = new ViewerInfo();
            const loaded = vi.loadFromSession();
            if (loaded && activeRoute) {

              const paramsQ = activeRoute.params.pipe(take(1));
              const params: Params = await lastValueFrom(paramsQ);

              if (params.companyID && vi.company && params.companyID !== vi.company.companyID) {
                this.logOut();
              }

              if (params.uid && vi.uid && params.uid !== vi.uid) {
                this.logOut();
              }

              this.viewerInfo.next(vi);

            }

          }

        }
      } else {
        this.logOut();
      }
    });

  }

  async idTokenResult(): Promise<any | null> {
    const query = this.auth.idTokenResult.pipe(take(1));
    const idToken = await lastValueFrom(query);
    return idToken;
  }

  pathForClaimLevel(level: ClaimLevelEnum, companyID: string | undefined = '', uid: string | undefined = ''): Array<string> {

    let pathArray = ['/'];
    switch (level) {
      case ClaimLevelEnum.employee:
        pathArray = ['auth', 'employee', companyID, uid, 'home'];
        break;
      case ClaimLevelEnum.role2:
        pathArray = ['auth', 'hraux', companyID, 'hraux-employees-edit'];
        break;
      case ClaimLevelEnum.role3:
        pathArray = ['auth', 'hr', companyID, 'hr-employees-edit'];
        break;
      case ClaimLevelEnum.role4:
        pathArray = ['auth', 'ss', companyID, 'ss-employees-list'];
        break;
      case ClaimLevelEnum.supervisor:
        pathArray = ['auth', 'supervisor', companyID, 'home'];
        break;
      case ClaimLevelEnum.administrator:
        pathArray = ['auth', 'admin', companyID, 'admin-employees-edit'];
        break;
      case ClaimLevelEnum.globalAdmin:
        pathArray = ['auth', 'ga', 'company-list'];
        break;
      default:
        break;
    }

    return pathArray;

  }

  async logOut(company: Company | undefined = undefined): Promise<void> {

    LocalHelpers.unsubscribe(this.authSubscription);

    if (company && company.services && company.services.ssoAccess.enabled) {
      await this.auth.signOut();
      LocalStorage.reset();
      this.router.navigate(['/account/' + company.companyID + '/okta/logout']);
    } else {

      await this.auth.signOut();
      LocalStorage.reset();
      await this.router.navigate(['/account/login']);

    }

  }

  async signInWithToken(data: ServerCall): Promise<void> {

    if (data.result.token) {

      const vi = new ViewerInfo();
      vi.loadFromLoginResult(data.result);

      console.log(data.result);

      const saved = vi.saveForSession();
      if (saved) {
        await this.auth.signInWithCustomToken(data.result.token)
      }

    }
  }

  async validateToken(token: string): Promise<ServerCall> {

    const serverCall = new ServerCall();
    serverCall.params = { token: token };
    const call = await this.req.buildRequestAsync('user', 'accessInfo', serverCall.toAny());
    return call;
  }

  async accessToken(uid: string, pass: string): Promise<ServerCall> {
    const serverCall = new ServerCall();
    serverCall.params = { userID: uid, password: pass };
    const call = await this.req.buildRequestAsync('user', 'login', serverCall.toAny());
    return call;
  }

  requestPasswordReset(userID: string): Promise<ServerCall> {
    const serverCall = new ServerCall();
    serverCall.params = { userID: userID };
    return this.req.buildRequestAsync('user', 'requestPasswordReset', serverCall.toAny());
  }

  setNewPassword(uid: string, password: string): Promise<ServerCall> {
    const serverCall = new ServerCall();
    serverCall.params = { uid: uid, password: password };
    return this.req.buildRequestAsync('user', 'resetPassword', serverCall.toAny());
  }

  async shouldLoadFromParams(path: ActivatedRoute, claims: Claim): Promise<boolean> {

    const allowedPaths: any = {
      10: ['admin', 'supervisor', 'hr', 'accounting', 'employee'],
      9: [],
      8: ['supervisor', 'hr', 'accounting', 'employee'],
      7: ['employee'],
      6: [],
      5: [],
      4: ['employee'],
      3: ['employee'],
      2: ['employee'],
      1: ['']
    };

    const url = await lastValueFrom(path.url.pipe(take(1)));
    if (url && url.length) {
      const segment = url[0];
      const paths: Array<string> = allowedPaths.hasOwnProperty(claims.maxClaimLevel) ? allowedPaths[claims.maxClaimLevel] : [];

      return paths.some(p => p === segment.path);

    }

    return false;

  }

  async maxLevel(): Promise<ClaimLevelEnum> {

    const idToken = await this.idTokenResult();
    if (idToken) {

      const claim: Claim = new Claim(idToken.claims.claim);
      return claim.maxClaimLevel;

    }

    return ClaimLevelEnum.notSet;

  }

}
